import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material'

import TransactionDataGrid from '../../components/Transactions/TransactionDataGrid'
import TransactionFilters from '../../components/Transactions/TransactionFilters'
import TransactionDetailsDialog from '../../components/Transactions/TransactionDetailsDialog'
import { useTransactions } from '../../hooks/useTransactions'
import { useTransactionActions } from '../../hooks/useTransactionActions'

const TransactionManagement = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    dateRange: null,
    amountRange: [0, 1000],
  })
  const [showFilters, setShowFilters] = useState(false)
  const [detailsDialog, setDetailsDialog] = useState({ open: false, transaction: null })

  const {
    data: transactionsData,
    isLoading,
    error,
    refetch,
  } = useTransactions({
    search: searchTerm,
    filters,
    page: 1,
    limit: 25,
  })

  const { exportTransactions } = useTransactionActions()

  const handleSearch = (value) => {
    setSearchTerm(value)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleViewDetails = (transaction) => {
    setDetailsDialog({
      open: true,
      transaction,
    })
  }

  const handleExport = async () => {
    try {
      await exportTransactions(filters)
    } catch (error) {
      console.error('Export error:', error)
    }
  }

  const getTypeChip = (type) => {
    const typeConfig = {
      purchase: { label: 'Satın Alma', color: 'success', icon: <TrendingUpIcon /> },
      rental: { label: 'Kiralama', color: 'primary', icon: <ReceiptIcon /> },
      admin_adjustment: { label: 'Admin Düzeltme', color: 'warning', icon: <AttachMoneyIcon /> },
    }
    
    const config = typeConfig[type] || { label: type, color: 'default', icon: null }
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small"
        icon={config.icon}
      />
    )
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      completed: { label: 'Tamamlandı', color: 'success' },
      pending: { label: 'Beklemede', color: 'warning' },
      failed: { label: 'Başarısız', color: 'error' },
      cancelled: { label: 'İptal Edildi', color: 'default' },
    }
    
    const config = statusConfig[status] || { label: status, color: 'default' }
    return <Chip label={config.label} color={config.color} size="small" />
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              İşlem Yönetimi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tüm finansal işlemleri görüntüleyin ve yönetin
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isLoading}
            >
              Yenile
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Dışa Aktar
            </Button>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="İşlem ara (email, ID, transaction ID...)"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtreler
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Filter Panel */}
        {showFilters && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <TransactionFilters
                filters={filters}
                onChange={handleFilterChange}
                onReset={() => setFilters({
                  type: 'all',
                  status: 'all',
                  dateRange: null,
                  amountRange: [0, 1000],
                })}
              />
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                {transactionsData?.stats?.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam İşlem
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                {transactionsData?.stats?.purchases || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Satın Alma
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                {transactionsData?.stats?.rentals || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kiralama
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                {transactionsData?.stats?.totalRevenue || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Gelir (Kredi)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Transactions Data Grid */}
      <Card>
        <TransactionDataGrid
          transactions={transactionsData?.transactions || []}
          isLoading={isLoading}
          error={error}
          onViewDetails={handleViewDetails}
          pagination={{
            total: transactionsData?.total || 0,
            page: transactionsData?.page || 1,
            limit: transactionsData?.limit || 25,
          }}
          getTypeChip={getTypeChip}
          getStatusChip={getStatusChip}
        />
      </Card>

      {/* Transaction Details Dialog */}
      <TransactionDetailsDialog
        open={detailsDialog.open}
        transaction={detailsDialog.transaction}
        onClose={() => setDetailsDialog({ open: false, transaction: null })}
        getTypeChip={getTypeChip}
        getStatusChip={getStatusChip}
      />
    </Box>
  )
}

export default TransactionManagement